import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { getFormOptions, IFormOptions } from '@app/shared/utils/form-utils';
import { IEnumFormControlConfig } from '@app/core/interfaces/form-control-config';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'gc-floating-popup-tabs',
  imports: [SelectButtonModule, FormsModule, ReactiveFormsModule, ButtonModule, TranslatePipe],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './floating-popup-tabs.component.html',
})
export class FloatingPopupTabsComponent<T extends string = string> implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  public facade = input.required<IEnumFormControlConfig<T>>();

  private facade$ = toObservable(this.facade);

  public tabsFormGroup?: FormGroup<{
    tab: FormControl<T | null>;
  }>;

  public tabOptions?: IFormOptions<T>[];

  ngOnInit(): void {
    this.facade$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.initComponent();
    });
  }

  private initComponent() {
    const facade = this.facade();

    this.tabsFormGroup = new FormGroup({
      tab: new FormControl<T | null>(null, [Validators.required]),
    });

    this.tabOptions = getFormOptions<T>(facade.values, facade.localize);

    this.initSubscriptions();
  }

  private initSubscriptions() {
    const facade = this.facade();

    facade
      .getValue()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.tabsFormGroup?.controls['tab'].setValue(value || facade.defaultValue, {
          emitEvent: false,
        });
      });

    this.tabsFormGroup?.controls['tab'].valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      facade.valueChange(value || facade.defaultValue);
      queueMicrotask(() => this.cdr.detectChanges());
    });
  }
}
